import { setCustomerAuthToken } from '@agos/api-client'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * The signed-in customer.
 *
 * Ported from `use-customer-auth`, whose two good decisions are kept and whose one loose end is closed.
 *
 * **Kept: a 401 signs you out, a dead connection does not.** `refreshUser` clears the session on a
 * non-OK answer and leaves it alone when `fetch` throws. That split is right — a customer on a train
 * losing signal is not a customer whose session expired — and it is the sort of thing a rewrite
 * flattens into one `catch`.
 *
 * **Kept: `partialize`.** Only the user and the token are written; `isLoading` is not, so a reload never
 * restores a spinner.
 *
 * **Closed: `fk_last_email`.** Legacy writes the customer's address to localStorage outside the store to
 * prefill the next sign-in, and never removes it — so a shared device names the last person who used it
 * after they have deliberately left. The prefill is genuinely useful and is kept; signing out clears it.
 */

export interface CustomerUser {
  id: number
  email: string | null
  name: string | null
  phone: string | null
  pointsBalance: number
  loyaltyLevel?: string
  /** Set by an administrator, read-only here. `null` means none. */
  personalDiscountType?: 'percent' | 'fixed' | null
  personalDiscountValue?: number
  /** False by default: the permanent discount **replaces** other discounts rather than stacking. */
  personalDiscountStackable?: boolean
}

const LAST_EMAIL_KEY = 'fk_last_email'

export function rememberLastEmail(email: string | null): void {
  try {
    if (email === null || email === '') localStorage.removeItem(LAST_EMAIL_KEY)
    else localStorage.setItem(LAST_EMAIL_KEY, email)
  } catch {
    /* private mode */
  }
}

export function lastEmail(): string {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) ?? ''
  } catch {
    return ''
  }
}

interface CustomerAuthState {
  user: CustomerUser | null
  token: string | null
  signIn: (user: CustomerUser, token: string) => void
  /** The session is gone: a 401, or a deliberate sign-out. */
  signOut: () => void
  update: (user: CustomerUser) => void
}

/**
 * The signed-in customer's id, or `null` when nobody is - a selector for {@link useCustomerAuth}, `FRONTEND.md`
 * §9.737.
 *
 * The push offer remembers a customer's answer under this id, and `usePush` and the dialog that asks both read it
 * through this one function rather than spelling it twice. **A token with no id reads as nobody**: a session whose
 * answers cannot be stored would open a dialog that *Nie* and *Zavrieť* could not close.
 */
export function signedInCustomerId(auth: Pick<CustomerAuthState, 'token' | 'user'>): number | null {
  if (auth.token === null || auth.user === null) return null
  return typeof auth.user.id === 'number' ? auth.user.id : null
}

export const useCustomerAuth = create<CustomerAuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      signIn: (user, token) => {
        rememberLastEmail(user.email)
        // `customFetch` reads the bearer from storage rather than from this store, so the token has to
        // be put where it will look. Legacy attached the header by hand at every call site, which is
        // why three of its authed screens each do it slightly differently.
        setCustomerAuthToken(token)
        set({ user, token })
      },
      signOut: () => {
        // The one thing legacy leaves behind. A shared device should not name the person who just left.
        rememberLastEmail(null)
        setCustomerAuthToken(null)
        set({ user: null, token: null })
      },
      update: (user) => {
        rememberLastEmail(user.email)
        set({ user })
      },
    }),
    {
      name: 'agos-customer-auth',
      version: 1,
      partialize: (state) => ({ user: state.user, token: state.token }),
    },
  ),
)
